import { createContext, ReactNode, useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useGame } from "../game/GameContext";
import { useProject } from "../project/ProjectContext";
import { Game, WordleConfiguration } from "../../gql/graphql";
import { hasAttributes } from "../../graphql/helpers/GameWithAttributes";

interface StepContextType {
  currentStep: number;
  setCurrentStep: (selectedStep: number, customGame?: Game) => Promise<void>;
  isShareModalOpen: boolean;
  gamesToConfigure: Game[];
  isAttributesNumberErrorModalOpen: boolean;
}

const StepContext = createContext<StepContextType | undefined>(undefined);

const isGameInvalid = (game: Game) => {
  const configuration = game.configuration;
  const { __typename: gameKind } = configuration;
  const attributes = hasAttributes(game) ? game.configuration.attributes : [];

  if (gameKind === "WordopolisConfiguration") {
    return attributes.length < 1;
  }
  if (gameKind === "QuickRankConfiguration") {
    return attributes.length < 2;
  }
  if (gameKind === "RankifierConfiguration") {
    return attributes.length < configuration.cardsPerHand;
  }
  if (gameKind === "SwiperConfiguration") {
    return attributes.length < 1;
  }
  if (gameKind === "WordleConfiguration") {
    const configuration = game.configuration as WordleConfiguration;
    return configuration.word === "";
  }
};

export const StepProvider = ({ children }: { children: ReactNode }) => {
  const { submit: submitProject } = useProject();
  const { games, currentGame } = useGame();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = Number(searchParams.get("step")) || 1;

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAttributesNumberErrorModalOpen, setIsAttributesNumberErrorModalOpen] = useState(false);

  const isStep2Comleted = games.length > 0;
  const gamesToConfigure = games.filter((game) => isGameInvalid(game));
  const isConfigurationCompleted = gamesToConfigure.length === 0;
  const isGameSelected = Boolean(currentGame);

  const tryToChangeStep = async (selectedStep: number, customGame?: Game): Promise<number> => {
    if (currentStep === 1) {
      const project = await submitProject();
      if (!project) return 1;
      if (currentStep === 1) {
        searchParams.set("id", project.id);
      }
    }

    if (!isStep2Comleted && selectedStep > 2) {
      return 2;
    }

    if (selectedStep >= 3 && (!isGameSelected || customGame)) {
      searchParams.set("gameId", customGame ? customGame.id : games[0].id);
    }

    // If validation is successful, proceed to selected step
    return selectedStep;
  };

  const handleSetCurrentStep = async (selectedStep: number, customGame?: Game): Promise<void> => {
    setIsAttributesNumberErrorModalOpen(false);
    if (selectedStep > 6 && !isConfigurationCompleted) {
      setIsAttributesNumberErrorModalOpen(true);
      return;
    } else if (selectedStep > 6) {
      setIsShareModalOpen(true);
      return;
    }
    if (selectedStep < 1 || selectedStep > 6) {
      return;
    }
    const nextStep = await tryToChangeStep(selectedStep, customGame);

    searchParams.set("step", nextStep.toString());
    setSearchParams(searchParams);
  };

  return (
    <StepContext.Provider
      value={{
        currentStep,
        setCurrentStep: handleSetCurrentStep,
        isShareModalOpen,
        isAttributesNumberErrorModalOpen,
        gamesToConfigure,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};

export const useStep = () => {
  const context = useContext(StepContext);
  if (context === undefined) {
    throw new Error("useStep must be used within a StepProvider");
  }
  return context;
};
